import type { FunctionComponent } from "react"

import { useParams } from "next/navigation"

import IconArrowDown from "@jobteaser/ui-kit/assets/pictograms/jds_pictogram_job_empty.svg"
import { EmptyState } from "@jobteaser/ui-kit/components/EmptyState/EmptyState"

import { useCompanyFollowTrack } from "@/modules/companies/client/hooks/tracking/useCompanyFollowTrack"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { useCompanyFollowerFetch } from "@/modules/companies/client/hooks/useCompanyFollowerFetch"
import { useFollowCompany } from "@/modules/companies/client/hooks/useFollowCompany"
import { useJobAdsSearchHasFilter } from "@/modules/companies/client/hooks/useJobAdsSearchHasFilter"
import { COMPANY_BASENAME } from "@/modules/companies/client/utils/routing"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { USERS_SIGNIN_PATH } from "@/modules/user/client/constants"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

export const JobAdsEmptyStates: FunctionComponent = () => {
  const locale = useLocale()
  const { dataCurrentUser } = useCurrentUserFetch()
  const companyFollowTrack = useCompanyFollowTrack()
  const { dataCompany } = useCompanyFetch()
  const { dataCompanyFollower } = useCompanyFollowerFetch()
  const { followCompany, isLoadingFollowCompany } = useFollowCompany()
  const { t } = useTranslation(["companies_client"])
  const { companySlug } = useParams()
  const hasFilters = useJobAdsSearchHasFilter()
  const hasContactEmailAddress = dataCompany?.page.contactEmailAddress

  const isFollowingCompany = !!dataCompanyFollower?.userInfo?.followsCompany
  const isAuthorizedToFollow = !!dataCompanyFollower?.userInfo?.authorizedToFollow

  let title = ""
  let description = ""
  let cta = null
  let testId = ""

  if (!hasFilters) {
    title = t("companies_client.jobAds.emptyStates.title.noOffers")
    description = t("companies_client.jobAds.emptyStates.description.noOffers")
    testId = "company_job_ads_empty_states_no_offer"

    if (hasContactEmailAddress) {
      cta = (
        <EmptyState.Button
          component="a"
          target="_blank"
          rel="noopener nofollow noreferrer"
          href={`mailto:${dataCompany.page.contactEmailAddress}`}
        >
          {t("companies_client.jobAds.emptyStates.action.candidate")}
        </EmptyState.Button>
      )
      testId = "company_job_ads_empty_states_mail"
    } else if (isAuthorizedToFollow && !isFollowingCompany) {
      cta = (
        <EmptyState.Button
          isLoading={isLoadingFollowCompany}
          onClick={() => {
            companyFollowTrack(true)
            followCompany(true)
          }}
        >
          {t("companies_client.jobAds.emptyStates.action.follow")}
        </EmptyState.Button>
      )
      testId = "company_job_ads_empty_states_follow"
    } else if (!dataCurrentUser?.isLogged) {
      cta = (
        <EmptyState.Button component="a" href={`/${locale}/${USERS_SIGNIN_PATH}`}>
          {t("companies_client.jobAds.emptyStates.action.follow")}
        </EmptyState.Button>
      )
      testId = "company_job_ads_empty_states_follow_unlogged"
    }
  } else {
    title = t("companies_client.jobAds.emptyStates.title.noFilteredOffers")
    description = t("companies_client.jobAds.emptyStates.description.noFilteredOffers")
    cta = (
      <EmptyState.Button
        component={Link}
        href={`/${locale}/${COMPANY_BASENAME}/${companySlug}/job-offers`}
        scroll={false}
        shallow
      >
        {t("companies_client.jobAds.emptyStates.action.resetFilters")}
      </EmptyState.Button>
    )
    testId = "company_job_ads_empty_states_reset_filters"
  }

  return (
    <EmptyState
      pictogram={IconArrowDown}
      title={title}
      description={description}
      data-testid={`company_job_ads_empty_states ${testId}`}
    >
      {cta}
    </EmptyState>
  )
}
